import { ApplicationStore } from "../store/ApplicationStore";
import { CompositionService } from "./CompositionService";
import { isScriptClip } from "../../engine/clips/script";
import { ProjectAssetsStore } from "../../state/ProjectAssetsStore";
import { FocusStore } from "../../state/FocusStore";
import { ScriptLayerStore } from "../store/ScriptLayerStore";
import { CaptionsClip } from "../../engine/clips/captions";
import { TextClip } from "../../engine/clips/text/text";
import { PlaceholderClip } from "../../engine/clips/placeholder/placeholder";
import { Timestamp } from "../../engine/models";
import { mapPlacedSourceEntryClipLocalToSourceTime } from "../../engine/utils/placedSourceTimeMapping";
import { DEFAULT_LAYOUT_PACK_ID, formatLayoutRef, normalizeLayoutId, parseLayoutRef } from "../layouts/LayoutPacks";

export class LayoutService {
    private static readonly LAYOUT_MANAGED_PREFIX = "__libretto_layout__";
    private static readonly layoutManagedAddInFlight = new Map<string, Promise<void>>();

    private static getCanvasAspectPresetFromSize(
        w: number,
        h: number
    ): "horizontal" | "vertical" | "square" {
        const ww = Number(w);
        const hh = Number(h);
        if (!Number.isFinite(ww) || !Number.isFinite(hh) || ww <= 0 || hh <= 0) return "horizontal";
        const ratio = ww / hh;
        if (Math.abs(ratio - 1) < 0.06) return "square";
        return ratio < 1 ? "vertical" : "horizontal";
    }

    private static getTrackRecordByTrackId(trackId?: string): any | undefined {
        const id = String(trackId ?? "");
        if (!id) return undefined;
        return (ProjectAssetsStore.projectTracks ?? []).find((t: any) => {
            const tid = String(t?.id ?? "");
            const tTrackId = String(t?.trackId ?? "");
            return tid === id || tTrackId === id;
        });
    }

    private static mapScriptVideoClipLocalToSourceTime(scriptClip: any, sourceIndex: number, clipLocalSec: number): number | null {
        try {
            const entries: any[] = typeof scriptClip?.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
            const entry = entries?.[sourceIndex];
            if (!entry) return null;
            return mapPlacedSourceEntryClipLocalToSourceTime(entry, clipLocalSec);
        } catch {}
        return null;
    }

    private static buildLayoutManagedClipName(args: {
        packId?: string;
        layout: string;
        role: string;
        sceneKey: string;
        aspectPreset: "horizontal" | "vertical" | "square";
    }): string {
        const packId = String(args.packId ?? DEFAULT_LAYOUT_PACK_ID).trim().toLowerCase() || DEFAULT_LAYOUT_PACK_ID;
        const layout = String(args.layout ?? "").trim().toLowerCase();
        const role = String(args.role ?? "").trim().toLowerCase();
        const sceneKey = String(args.sceneKey ?? "__start__");
        const aspect = String(args.aspectPreset);
        return `${LayoutService.LAYOUT_MANAGED_PREFIX}:pack=${packId}:layout=${layout}:role=${role}:scene=${sceneKey}:aspect=${aspect}`;
    }

    private static findClipByName(name: string): any | null {
        const comp: any = CompositionService.composition;
        if (!comp) return null;
        const target = String(name ?? "");
        if (!target) return null;

        try {
            for (const tr of comp.tracks ?? []) {
                for (const c of (tr as any)?.clips ?? []) {
                    if (String((c as any)?.name ?? "") === target) return c as any;
                }
            }
        } catch {}

        return null;
    }

    private static hideLayoutManagedClipsForSceneAspect(args: {
        sceneKey: string;
        aspectPreset: "horizontal" | "vertical" | "square";
    }): void {
        const comp: any = CompositionService.composition;
        if (!comp) return;
        const sceneKey = String(args.sceneKey ?? "__start__");
        const aspect = String(args.aspectPreset);
        if (!sceneKey || !aspect) return;

        const sceneToken = `scene=${sceneKey}`;
        const aspectToken = `aspect=${aspect}`;

        try {
            for (const tr of comp.tracks ?? []) {
                for (const c of (tr as any)?.clips ?? []) {
                    if (!c) continue;
                    const name = String((c as any)?.name ?? "");
                    if (!name.startsWith(LayoutService.LAYOUT_MANAGED_PREFIX)) continue;
                    if (!name.includes(sceneToken)) continue;
                    if (!name.includes(aspectToken)) continue;
                    try {
                        if (typeof (c as any).alpha === "number") (c as any).alpha = 0;
                    } catch {}
                    try {
                        CompositionService.notifyClipUpdated(c);
                    } catch {}
                }
            }
        } catch {}
    }

    private static getSceneInfo(scriptClip: any, sceneIndex: number): { sceneKey: string; srcStartSec: number; srcEndSec: number } | null {
        try {
            const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
            if (!Array.isArray(scenes) || scenes.length === 0) return null;
            const scene =
                scenes.find((s: any) => Number(s?.index) === Number(sceneIndex)) ??
                scenes[sceneIndex];
            if (!scene) return null;
            const srcStartSec = Number(scene?.startSec ?? 0);
            const srcEndSec = Number(scene?.endSec ?? srcStartSec);
            if (!Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec) || srcEndSec <= srcStartSec) return null;
            const sceneKey = scene?.startSplitId ? String(scene.startSplitId) : "__start__";
            return { sceneKey, srcStartSec, srcEndSec };
        } catch {}
        return null;
    }

    private static getSceneAbsTimeRangeFromSegments(scriptClip: any, srcStartSec: number, srcEndSec: number): { startSec: number; endSec: number } | null {
        try {
            const segs: any[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
            if (!segs.length) return null;

            let start = Number.POSITIVE_INFINITY;
            let end = Number.NEGATIVE_INFINITY;

            for (const seg of segs) {
                const s0 = Number((seg as any)?.srcStartSec ?? Number.NaN);
                const s1 = Number((seg as any)?.srcEndSec ?? Number.NaN);
                const c0 = Number((seg as any)?.compStartSec ?? Number.NaN);
                if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
                if (s1 <= s0) continue;

                const overlapStart = Math.max(srcStartSec, s0);
                const overlapEnd = Math.min(srcEndSec, s1);
                if (overlapEnd <= overlapStart) continue;

                const compStart = c0 + (overlapStart - s0);
                const compEnd = c0 + (overlapEnd - s0);
                start = Math.min(start, compStart);
                end = Math.max(end, compEnd);
            }

            if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
            return { startSec: start, endSec: end };
        } catch {}
        return null;
    }

    private static ensureTextClip(args: {
        name: string;
        aspectPreset: "horizontal" | "vertical" | "square";
        startSec: number;
        endSec: number;
        x: number;
        y: number;
        maxWidth: number | string;
        textAlign: "left" | "center" | "right";
        // Only used when creating:
        defaultText: string;
        defaultFontSize: number;
        defaultFontWeight: "normal" | "bold";
    }): void {
        const comp: any = CompositionService.composition;
        if (!comp) return;
        const startSec = Number(args.startSec);
        const endSec = Number(args.endSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) return;

        const existing = LayoutService.findClipByName(args.name);
        const applyCommon = (clip: any, { setDefaults }: { setDefaults: boolean }) => {
            try { (clip as any).__librettoLayoutAspectPreset = args.aspectPreset; } catch {}

            // Timing
            try { clip.start = Timestamp.fromSeconds(startSec); } catch {}
            try { clip.stop = Timestamp.fromSeconds(endSec); } catch {}

            // Visual placement
            try { clip.position = { x: args.x, y: args.y } as any; } catch {}
            try { clip.maxWidth = args.maxWidth as any; } catch {}
            try { clip.textAlign = args.textAlign as any; } catch {}

            // Ensure visible when the layout is applied.
            try { if (typeof clip.alpha === "number") clip.alpha = 1; } catch {}

            if (setDefaults) {
                try { clip.text = args.defaultText; } catch {}
                try {
                    clip.textStyle = {
                        fontFamily: "Inter",
                        fontSize: args.defaultFontSize,
                        fontWeight: args.defaultFontWeight,
                        fontStyle: "normal",
                        fillColor: "#FFFFFF",
                        stroke: { color: "#000000", width: 4, join: "round" },
                    } as any;
                } catch {}
            }

            try { CompositionService.notifyClipUpdated(clip, { timingChanged: true }); } catch {}
        };

        if (existing && String((existing as any)?.type ?? "") === "text") {
            applyCommon(existing, { setDefaults: false });
            return;
        }

        if (LayoutService.layoutManagedAddInFlight.has(args.name)) return;

        const clip = new TextClip({
            name: args.name,
            text: args.defaultText,
            textAlign: args.textAlign as any,
            maxWidth: args.maxWidth as any,
            textStyle: {
                fontFamily: "Inter",
                fontSize: args.defaultFontSize,
                fontWeight: args.defaultFontWeight,
                fontStyle: "normal",
                fillColor: "#FFFFFF",
                stroke: { color: "#000000", width: 4, join: "round" },
            } as any,
            position: { x: args.x, y: args.y } as any,
            start: Timestamp.fromSeconds(startSec),
            stop: Timestamp.fromSeconds(endSec),
        } as any);

        try { (clip as any).__librettoLayoutAspectPreset = args.aspectPreset; } catch {}

        const addPromise = (async () => {
            try {
                await comp.add(clip as any);
                applyCommon(clip as any, { setDefaults: false });
            } catch {}
            finally {
                LayoutService.layoutManagedAddInFlight.delete(args.name);
            }
        })();
        LayoutService.layoutManagedAddInFlight.set(args.name, addPromise);
    }

    private static ensurePlaceholderClip(args: {
        name: string;
        aspectPreset: "horizontal" | "vertical" | "square";
        startSec: number;
        endSec: number;
        x: number;
        y: number;
        width: number;
        height: number;
        label?: string;
        placeholderKind?: "media" | "screen" | "camera";
    }): void {
        const comp: any = CompositionService.composition;
        if (!comp) return;
        const startSec = Number(args.startSec);
        const endSec = Number(args.endSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) return;

        const existing = LayoutService.findClipByName(args.name);
        const applyCommon = (clip: any, { setDefaults }: { setDefaults: boolean }) => {
            try { (clip as any).__librettoLayoutAspectPreset = args.aspectPreset; } catch {}
            try { clip.start = Timestamp.fromSeconds(startSec); } catch {}
            try { clip.stop = Timestamp.fromSeconds(endSec); } catch {}
            try { clip.position = { x: args.x, y: args.y } as any; } catch {}
            try { clip.width = args.width as any; } catch {}
            try { clip.height = args.height as any; } catch {}
            try { if (typeof clip.alpha === "number") clip.alpha = 1; } catch {}
            if (args.placeholderKind) {
                try { (clip as any).placeholderKind = args.placeholderKind; } catch {}
            }
            if (setDefaults && typeof args.label === "string") {
                try { (clip as any).label = args.label; } catch {}
            }
            try { CompositionService.notifyClipUpdated(clip, { timingChanged: true }); } catch {}
        };

        if (existing) {
            const existingType = String((existing as any)?.type ?? "");
            if (existingType === "placeholder") {
                applyCommon(existing, { setDefaults: false });
                return;
            }

            // Migration: older builds used a TextClip placeholder. If we encounter a layout-managed
            // non-placeholder clip here, treat it as a legacy placeholder and remove it.
            try {
                const isLayoutManaged = String((existing as any)?.name ?? "").startsWith(`${LayoutService.LAYOUT_MANAGED_PREFIX}:`);
                const isLegacyPlaceholder = isLayoutManaged && (existingType === "text" || existingType === "captions");
                if (isLegacyPlaceholder) {
                    CompositionService.deleteClipById(String((existing as any).id));
                } else {
                    // A real clip already occupies this slot name; do not create a placeholder.
                    return;
                }
            } catch {
                return;
            }
        }

        if (LayoutService.layoutManagedAddInFlight.has(args.name)) return;

        const clip = new PlaceholderClip(
            {
                name: args.name,
                label: args.label ?? "Add media",
                placeholderKind: args.placeholderKind ?? "media",
                position: { x: args.x, y: args.y } as any,
                width: args.width as any,
                height: args.height as any,
                start: Timestamp.fromSeconds(startSec),
                stop: Timestamp.fromSeconds(endSec),
            } as any,
        );

        try { (clip as any).__librettoLayoutAspectPreset = args.aspectPreset; } catch {}

        const addPromise = (async () => {
            try {
                await comp.add(clip as any);
                applyCommon(clip as any, { setDefaults: false });
            } catch {}
            finally {
                LayoutService.layoutManagedAddInFlight.delete(args.name);
            }
        })();
        LayoutService.layoutManagedAddInFlight.set(args.name, addPromise);
    }

    private static ensureCaptionsClip(args: {
        name: string;
        aspectPreset: "horizontal" | "vertical" | "square";
        scriptClipId: string;
        startSec: number;
        endSec: number;
        x: number;
        y: number;
        maxWidth: number | string;
    }): void {
        const comp: any = CompositionService.composition;
        if (!comp) return;

        const existing = LayoutService.findClipByName(args.name);

        const applyCommon = (clip: any) => {
            const startSec = Number(args.startSec);
            const endSec = Number(args.endSec);
            if (Number.isFinite(startSec) && Number.isFinite(endSec) && endSec > startSec) {
                try { clip.start = Timestamp.fromSeconds(startSec); } catch {}
                try { clip.stop = Timestamp.fromSeconds(endSec); } catch {}
            }

            try { clip.scriptClipId = args.scriptClipId; } catch {}
            try { clip.timingScope = "scene"; } catch {}

            try { clip.position = { x: args.x, y: args.y } as any; } catch {}
            try { clip.maxWidth = args.maxWidth as any; } catch {}

            try { if (typeof clip.alpha === "number") clip.alpha = 1; } catch {}

            try { CompositionService.notifyClipUpdated(clip, { timingChanged: true }); } catch {}
        };

        if (existing && String((existing as any)?.type ?? "") === "captions") {
            applyCommon(existing);
            return;
        }

        if (LayoutService.layoutManagedAddInFlight.has(args.name)) return;

        const clip = new CaptionsClip({
            name: args.name,
            scriptClipId: args.scriptClipId,
            timingScope: "scene",
            position: { x: args.x, y: args.y } as any,
            maxWidth: args.maxWidth as any,
            start: Timestamp.fromSeconds(Number(args.startSec)),
            stop: Timestamp.fromSeconds(Number(args.endSec)),
            words: [],
        } as any);

        try { (clip as any).__librettoLayoutAspectPreset = args.aspectPreset; } catch {}

        const addPromise = (async () => {
            try {
                await comp.add(clip as any);
                applyCommon(clip as any);
            } catch {}
            finally {
                LayoutService.layoutManagedAddInFlight.delete(args.name);
            }
        })();
        LayoutService.layoutManagedAddInFlight.set(args.name, addPromise);
    }

    /**
     * Apply a layout preset to a script-layer scene by writing deterministic per-scene overrides.
     * This is intentionally conservative for V1: it writes per-scene overrides for script-layer sources and
     * (for some layouts) creates/updates layout-managed text clips.
     */
    static applySceneLayout(sceneIndex: number, layoutName: string | null): void {
        const scriptClip: any = CompositionService.getScriptLayerClip();
        if (!scriptClip || !isScriptClip(scriptClip)) return;
        try { (scriptClip as any).__librettoAutoDefaultPlacementUserEdited = true; } catch {}

        const comp: any = CompositionService.composition;
        const cw = Number(comp?.settings?.width ?? comp?.width ?? 1920);
        const ch = Number(comp?.settings?.height ?? comp?.height ?? 1080);
        if (!(cw > 0 && ch > 0)) return;

        const aspectPreset = LayoutService.getCanvasAspectPresetFromSize(cw, ch);

        const parsed = parseLayoutRef(layoutName);
        const packId = String(parsed?.packId ?? DEFAULT_LAYOUT_PACK_ID).trim() || DEFAULT_LAYOUT_PACK_ID;
        const layoutIdRaw = parsed?.layoutId ?? String(layoutName ?? "");
        const normalizedLayoutId = normalizeLayoutId(layoutIdRaw);
        const layoutRefToPersist = normalizedLayoutId ? formatLayoutRef(packId, normalizedLayoutId) : null;

        // "None" / empty: clear per-scene overrides for this aspect preset.
        if (!normalizedLayoutId || normalizedLayoutId === "none") {
            try {
                scriptClip.setSceneLayout?.(sceneIndex, null, aspectPreset);
            } catch {}
            try {
                scriptClip.clearSceneVisualOverrides?.(sceneIndex, aspectPreset);
            } catch {}
            try {
                // Await async clip updates so the new layout state is visible immediately while paused.
                void CompositionService.composition.recomputeFrameAtCurrentTimeAsync?.();
            } catch {}
            try {
                CompositionService.notifyClipUpdated(scriptClip);
            } catch {}
            try { ScriptLayerStore.bumpVisualPropsVersion(); } catch {}
            return;
        }

        const sources: any[] = typeof scriptClip.getVideoSources === "function" ? scriptClip.getVideoSources() : [];
        if (!Array.isArray(sources) || sources.length === 0) return;

        const selected = ApplicationStore.selectedVisualElement;
        const layout = normalizedLayoutId;

        // Persist per-aspect layout selection on the scene.
        try {
            scriptClip.setSceneLayout?.(sceneIndex, layoutRefToPersist, aspectPreset);
        } catch {}

        // Scene info (stable key + source-time bounds)
        const sceneInfo = LayoutService.getSceneInfo(scriptClip, sceneIndex);
        const sceneKey = sceneInfo?.sceneKey ?? "__start__";

        // Timeline range for this scene (absolute composition seconds).
        let sceneTimelineStart = 0;
        let sceneTimelineEnd = 0;
        try {
            const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
            const s =
                scenes.find((x: any) => Number(x?.index) === Number(sceneIndex)) ??
                scenes[sceneIndex];
            const clipStartAbs = Number((scriptClip as any)?.start?.seconds ?? 0);
            const t0 = Number(s?.timelineStartSec ?? 0);
            const t1 = Number(s?.timelineEndSec ?? t0);
            sceneTimelineStart = clipStartAbs + (Number.isFinite(t0) ? t0 : 0);
            sceneTimelineEnd = clipStartAbs + (Number.isFinite(t1) ? t1 : sceneTimelineStart);
        } catch {}

        // Hide any existing layout-managed clips for this scene/aspect (regardless of pack).
        // The active layout will re-enable whatever it needs (Intro/Chapter/etc).
        if (sceneInfo) {
            LayoutService.hideLayoutManagedClipsForSceneAspect({
                sceneKey: sceneInfo.sceneKey,
                aspectPreset,
            });
        }

        // Helper: resolve whether a source is a screen recording.
        const isScreenSource = (srcIdx: number): boolean => {
            try {
                const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
                const entries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
                const entry = entries[srcIdx];
                if (!entry?.input) return false;
                const cfg = cfgs.find((c: any) => c?.input === entry.input);
                const trackId = String(cfg?.assetId ?? "");
                const tr = LayoutService.getTrackRecordByTrackId(trackId);
                if (typeof tr?.isScreenRecording === "boolean") return Boolean(tr.isScreenRecording);
                const title = String(tr?.title ?? cfg?.name ?? "");
                return /screen/i.test(title);
            } catch {}
            return false;
        };

        const getTrackIdForSource = (srcIdx: number): string | null => {
            try {
                const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
                const entries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
                const entry = entries?.[srcIdx];
                if (!entry?.input) return null;
                const cfg = cfgs.find((c: any) => c?.input === entry.input);
                const trackId = String(cfg?.assetId ?? "");
                return trackId || null;
            } catch {}
            return null;
        };

        const getOrderedScriptSourceIndices = (): number[] => {
            const scriptClipId = String((scriptClip as any)?.id ?? "");
            const prefix = `script:${scriptClipId}:`;
            const fromPanel = ScriptLayerStore.getLayoutVisualOrder(scriptClipId);
            const ordered: number[] = [];

            for (const rowId of fromPanel) {
                const id = String(rowId ?? "");
                if (!id.startsWith(prefix)) continue;
                const raw = id.slice(prefix.length);
                const idx = Number(raw);
                if (!Number.isFinite(idx)) continue;
                const i = Math.floor(idx);
                if (i < 0 || i >= sources.length) continue;
                if (!ordered.includes(i)) ordered.push(i);
            }

            for (let i = 0; i < sources.length; i++) {
                if (!ordered.includes(i)) ordered.push(i);
            }

            return ordered;
        };

        const getOrderedEligibleScriptSourceIndices = (): number[] => {
            const ordered = getOrderedScriptSourceIndices();
            return ordered.filter((i) => !isScriptSourceHiddenByUser(i));
        };

        const getSourceIndexForSingle = (): number => {
            const selectedIndex =
                selected &&
                selected.clipId === scriptClip.id &&
                Number.isFinite(Number(selected.sourceIndex))
                    ? Math.floor(Number(selected.sourceIndex))
                    : -1;
            if (selectedIndex >= 0 && selectedIndex < sources.length && !isScriptSourceHiddenByUser(selectedIndex)) {
                return selectedIndex;
            }

            const orderedEligible = getOrderedEligibleScriptSourceIndices();
            if (orderedEligible.length > 0) {
                // Prefer a non-screen source for camera-focused layouts.
                return orderedEligible.find((i) => !isScreenSource(i)) ?? orderedEligible[0];
            }

            return 0;
        };

        const getDims = (srcIdx: number): { w: number; h: number } | null => {
            try {
                const originalDims =
                    typeof (scriptClip as any).getOriginalSourceDimensions === "function"
                        ? (scriptClip as any).getOriginalSourceDimensions(srcIdx)
                        : undefined;
                const currentVisual =
                    typeof (scriptClip as any).getVideoSourceVisual === "function"
                        ? (scriptClip as any).getVideoSourceVisual(srcIdx)
                        : undefined;
                const w = Number(originalDims?.width ?? currentVisual?.originalWidth ?? currentVisual?.width ?? 0);
                const h = Number(originalDims?.height ?? currentVisual?.originalHeight ?? currentVisual?.height ?? 0);
                if (!(w > 0 && h > 0)) return null;
                return { w, h };
            } catch {}
            return null;
        };

        const fitScale = (srcW: number, srcH: number, targetW: number, targetH: number): number => {
            const wr = targetW / srcW;
            const hr = targetH / srcH;
            return Math.min(wr, hr);
        };

        const coverScale = (srcW: number, srcH: number, targetW: number, targetH: number): number => {
            const wr = targetW / srcW;
            const hr = targetH / srcH;
            return Math.max(wr, hr);
        };

        const applyToSource = (srcIdx: number, props: any) => {
            try {
                if (typeof (scriptClip as any).setSceneVisualProperties === "function") {
                    (scriptClip as any).setSceneVisualProperties(sceneIndex, srcIdx, props);
                }
            } catch {}
        };

        const isScriptSourceHiddenByUser = (srcIdx: number): boolean => {
            try {
                const sceneProps = typeof (scriptClip as any).getSceneProperties === "function"
                    ? (scriptClip as any).getSceneProperties(sceneIndex)
                    : undefined;
                const preset = aspectPreset;
                const byAspect = (sceneProps as any)?.visualOverridesByAspect as Record<string, Map<number, any>> | undefined;
                const map =
                    (byAspect && (byAspect as any)[preset] && typeof (byAspect as any)[preset].get === "function"
                        ? (byAspect as any)[preset]
                        : undefined) ??
                    (preset === "horizontal" ? (sceneProps as any)?.visualOverrides : undefined);
                const ov = map?.get?.(srcIdx);
                return Boolean((ov as any)?.hiddenByUser);
            } catch {}
            return false;
        };

        const hideOtherSources = (keepIdx: number) => {
            try {
                for (let i = 0; i < (sources?.length ?? 0); i++) {
                    if (i === keepIdx) continue;
                    applyToSource(i, { alpha: 0 });
                }
            } catch {}
        };

        type NonScriptLayer = {
            clip: any;
            clipId: string;
            type: string;
            label: string;
            trackId: string | null;
            trackIndex: number;
            clipIndex: number;
            isScreen: boolean;
        };

        const getVisibleNonScriptLayersInScene = (): NonScriptLayer[] => {
            const result: NonScriptLayer[] = [];
            try {
                const compAny: any = CompositionService.composition;
                if (!compAny) return result;
                const overlaps = (start: number, end: number) => start < sceneTimelineEnd && end > sceneTimelineStart;

                for (let ti = 0; ti < (compAny?.tracks ?? []).length; ti++) {
                    const tr = compAny.tracks[ti];
                    const clips = (tr as any)?.clips ?? [];
                    for (let ci = 0; ci < clips.length; ci++) {
                        const c = clips[ci];
                        if (!c) continue;
                        if (String((c as any).id) === String((scriptClip as any).id)) continue;

                        const type = String((c as any).type ?? "");
                        if (!(type === "video" || type === "image" || type === "placeholder")) continue;

                        const name = String((c as any).name ?? "");
                        const isLayoutManaged = name.startsWith(LayoutService.LAYOUT_MANAGED_PREFIX);
                        if (isLayoutManaged) continue;

                        const start = Number((c as any)?.start?.seconds ?? 0);
                        const dur = Number((c as any)?.timelineDuration?.seconds ?? 0);
                        const end = start + Math.max(0, Number.isFinite(dur) ? dur : 0);
                        if (!overlaps(start, end)) continue;

                        const ov =
                            (c as any)?.sceneVisualOverridesByAspect?.[aspectPreset]?.[sceneKey] ??
                            (c as any)?.sceneVisualOverrides?.[sceneKey];
                        if (ov && typeof ov === "object" && (ov as any).hiddenByUser === true) continue;

                        const trackId = String((c as any)?.source?.id ?? "");
                        const track = LayoutService.getTrackRecordByTrackId(trackId);
                        const isScreen =
                            typeof track?.isScreenRecording === "boolean"
                                ? Boolean(track.isScreenRecording)
                                : /screen/i.test(String(track?.title ?? name));

                        result.push({
                            clip: c,
                            clipId: String((c as any).id),
                            type,
                            label: name || `${type} layer`,
                            trackId: trackId || null,
                            trackIndex: ti,
                            clipIndex: ci,
                            isScreen,
                        });
                    }
                }
            } catch {}

            // Prefer ordering from the V2 "Layers" panel when available so slot assignment is predictable.
            // Fall back to topmost-first (front track index 0 is topmost).
            const panelOrder = ScriptLayerStore.getLayoutVisualOrder(String((scriptClip as any)?.id ?? ""));
            const panelIndex = new Map(panelOrder.map((id, i) => [id, i]));
            result.sort((a, b) => {
                const aIdx = panelIndex.get(a.clipId);
                const bIdx = panelIndex.get(b.clipId);
                const aHas = typeof aIdx === "number";
                const bHas = typeof bIdx === "number";
                if (aHas || bHas) {
                    if (!aHas) return 1;
                    if (!bHas) return -1;
                    if (aIdx !== bIdx) return aIdx - bIdx;
                }

                if (a.trackIndex !== b.trackIndex) return a.trackIndex - b.trackIndex;
                return b.clipIndex - a.clipIndex;
            });
            return result;
        };

        const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

        const computeCoverCropFromFocus = (args: {
            srcAspect: number;
            targetAspect: number;
            focusRect: { x: number; y: number; w: number; h: number } | null;
        }): { x: number; y: number; width: number; height: number } | undefined => {
            const srcAspect = args.srcAspect;
            const targetAspect = args.targetAspect;
            if (!Number.isFinite(srcAspect) || !Number.isFinite(targetAspect) || targetAspect <= 0) return undefined;

            let cropW = 1;
            let cropH = 1;
            if (srcAspect > targetAspect) {
                cropW = clamp01(targetAspect / srcAspect);
                cropH = 1;
            } else {
                cropW = 1;
                cropH = clamp01(srcAspect / targetAspect);
            }

            const focus = args.focusRect;
            const fx0 = clamp01(Number(focus?.x ?? 0.5));
            const fy0 = clamp01(Number(focus?.y ?? 0.5));
            const fw = clamp01(Number(focus?.w ?? 0));
            const fh = clamp01(Number(focus?.h ?? 0));
            const fx1 = clamp01(fx0 + fw);
            const fy1 = clamp01(fy0 + fh);
            const fcx = clamp01(fx0 + (fx1 - fx0) / 2);
            const fcy = clamp01(fy0 + (fy1 - fy0) / 2);

            const minX = Math.max(0, fx1 - cropW);
            const maxX = Math.min(1 - cropW, fx0);
            const minY = Math.max(0, fy1 - cropH);
            const maxY = Math.min(1 - cropH, fy0);

            const desiredX = fcx - cropW / 2;
            const desiredY = fcy - cropH / 2;

            const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
            const x = minX <= maxX ? clamp(desiredX, minX, maxX) : clamp(desiredX, 0, 1 - cropW);
            const y = minY <= maxY ? clamp(desiredY, minY, maxY) : clamp(desiredY, 0, 1 - cropH);

            if (cropW >= 0.999 && cropH >= 0.999) return undefined;
            return { x, y, width: cropW, height: cropH };
        };

        const computeUniformZoomCropFromFocus = (args: {
            zoomFactor: number;
            focusRect: { x: number; y: number; w: number; h: number } | null;
        }): { x: number; y: number; width: number; height: number } | undefined => {
            const z = Number(args.zoomFactor ?? 1);
            if (!Number.isFinite(z) || z <= 1.0001) return undefined;

            const cropW = clamp01(1 / z);
            const cropH = cropW;
            if (cropW >= 0.999) return undefined;

            const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

            const focus = args.focusRect;
            if (!focus) {
                return {
                    x: clamp(0.5 - cropW / 2, 0, 1 - cropW),
                    y: clamp(0.5 - cropH / 2, 0, 1 - cropH),
                    width: cropW,
                    height: cropH,
                };
            }

            const fx0 = clamp01(Number(focus.x ?? 0.5));
            const fy0 = clamp01(Number(focus.y ?? 0.5));
            const fw = clamp01(Number(focus.w ?? 0));
            const fh = clamp01(Number(focus.h ?? 0));
            const fx1 = clamp01(fx0 + fw);
            const fy1 = clamp01(fy0 + fh);
            const fcx = clamp01(fx0 + (fx1 - fx0) / 2);
            const fcy = clamp01(fy0 + (fy1 - fy0) / 2);

            const minX = Math.max(0, fx1 - cropW);
            const maxX = Math.min(1 - cropW, fx0);
            const minY = Math.max(0, fy1 - cropH);
            const maxY = Math.min(1 - cropH, fy0);

            const desiredX = fcx - cropW / 2;
            const desiredY = fcy - cropH / 2;

            const x = minX <= maxX ? clamp(desiredX, minX, maxX) : clamp(desiredX, 0, 1 - cropW);
            const y = minY <= maxY ? clamp(desiredY, minY, maxY) : clamp(desiredY, 0, 1 - cropH);

            return { x, y, width: cropW, height: cropH };
        };

        const computeSquareCropFromFaceRect = (args: {
            srcW: number;
            srcH: number;
            faceRect: { x: number; y: number; w: number; h: number } | null;
            paddingFactor?: number;
        }): { x: number; y: number; width: number; height: number } | undefined => {
            const srcW = Number(args.srcW ?? 0);
            const srcH = Number(args.srcH ?? 0);
            if (!Number.isFinite(srcW) || !Number.isFinite(srcH) || srcW <= 0 || srcH <= 0) return undefined;

            const face = args.faceRect;
            const fx0 = clamp01(Number(face?.x ?? NaN));
            const fy0 = clamp01(Number(face?.y ?? NaN));
            const fwRaw = clamp01(Number(face?.w ?? NaN));
            const fhRaw = clamp01(Number(face?.h ?? NaN));
            if (!Number.isFinite(fx0) || !Number.isFinite(fy0) || !(fwRaw > 0) || !(fhRaw > 0)) return undefined;
            const fx1 = clamp01(fx0 + fwRaw);
            const fy1 = clamp01(fy0 + fhRaw);
            const fw = clamp01(fx1 - fx0);
            const fh = clamp01(fy1 - fy0);
            if (!(fw > 0) || !(fh > 0)) return undefined;

            const fcx = clamp01(fx0 + fw / 2);
            const fcy = clamp01(fy0 + fh / 2);

            const faceWpx = fw * srcW;
            const faceHpx = fh * srcH;
            const baseSizePx = Math.max(1, Math.max(faceWpx, faceHpx));
            const paddingFactor = Number(args.paddingFactor ?? 3.0);
            const maxSquarePx = Math.max(1, Math.min(srcW, srcH));
            const squarePx = Math.min(maxSquarePx, baseSizePx * (Number.isFinite(paddingFactor) ? paddingFactor : 3.0));

            const cropW = clamp01(squarePx / srcW);
            const cropH = clamp01(squarePx / srcH);
            if (cropW >= 0.999 && cropH >= 0.999) return undefined;

            const minX = Math.max(0, fx1 - cropW);
            const maxX = Math.min(1 - cropW, fx0);
            const minY = Math.max(0, fy1 - cropH);
            const maxY = Math.min(1 - cropH, fy0);

            const desiredX = fcx - cropW / 2;
            const desiredY = fcy - cropH / 2;

            const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
            const x = minX <= maxX ? clamp(desiredX, minX, maxX) : clamp(desiredX, 0, 1 - cropW);
            const y = minY <= maxY ? clamp(desiredY, minY, maxY) : clamp(desiredY, 0, 1 - cropH);

            return { x, y, width: cropW, height: cropH };
        };

        const clipLocalFocusTimeSec =
            sceneInfo && Number.isFinite(Number(sceneInfo.srcStartSec)) && Number.isFinite(Number(sceneInfo.srcEndSec))
                ? Math.max(0, (Number(sceneInfo.srcStartSec) + Number(sceneInfo.srcEndSec)) / 2)
                : 0;

        const getFocusRectForTrackId = (
            trackId: string | null,
            opts?: { scriptSourceIndex?: number }
        ): { x: number; y: number; w: number; h: number } | null => {
            if (!trackId) return null;

            let tSec = clipLocalFocusTimeSec;
            try {
                const idxRaw = opts?.scriptSourceIndex;
                if (Number.isFinite(Number(idxRaw))) {
                    const idx = Math.floor(Number(idxRaw));
                    const mapped = this.mapScriptVideoClipLocalToSourceTime(scriptClip, idx, clipLocalFocusTimeSec);
                    if (mapped != null && Number.isFinite(mapped)) tSec = Math.max(0, mapped);
                }
            } catch {}

            const rect =
                FocusStore.getRectAtTimeSec(trackId, tSec) ??
                FocusStore.getQuickSummary(trackId)?.rect ??
                null;
            if (!rect) return null;
            return { x: Number(rect.x ?? 0), y: Number(rect.y ?? 0), w: Number(rect.w ?? 0), h: Number(rect.h ?? 0) };
        };

        if (layout === "camera" || layout === "zoom" || layout === "speaker" || layout === "captions") {
            const srcIdx = getSourceIndexForSingle();
            const dims = getDims(srcIdx);
            if (!dims) return;

            // Camera is cover. Zoom is camera cover + implicit zoom (crop) of 115% (matches product spec).
            // Speaker/Captions default to cover with slight positioning tweaks for vertical/square.
            let scaler = coverScale(dims.w, dims.h, cw, ch);

            let x = cw / 2;
            let y = ch / 2;

	            if (layout === "speaker" && (aspectPreset === "vertical" || aspectPreset === "square")) {
	                // Leave room for a left-aligned speaker label (vertical + square previews).
	                x = cw * 0.58;
	            }

	            const crop =
	                layout === "zoom"
	                    ? computeUniformZoomCropFromFocus({
	                          zoomFactor: 1.15,
	                          focusRect: getFocusRectForTrackId(getTrackIdForSource(srcIdx), { scriptSourceIndex: srcIdx }),
	                      })
	                    : undefined;

            applyToSource(srcIdx, {
                width: dims.w * scaler,
                height: dims.h * scaler,
                x,
                y,
                alpha: 1,
                maskShape: "none",
                // Ensure zoom is done without changing bounds: crop into the source instead of resizing.
                // Passing `undefined` explicitly clears any prior per-scene crop from other layouts.
                crop,
            });
            hideOtherSources(srcIdx);

            if (layout === "speaker" && sceneInfo) {
                const absRange =
                    LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec) ?? {
                        startSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineStartSec ?? 0),
                        endSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineEndSec ?? 0),
                    };

                const base = Math.min(cw, ch);
                const font = Math.max(18, Math.round(base * 0.05));

                const labelName = LayoutService.buildLayoutManagedClipName({
                    packId,
                    layout: "speaker",
                    role: "label",
                    sceneKey: sceneInfo.sceneKey,
                    aspectPreset,
                });

	                const margin = Math.max(16, cw * 0.07);
	                const maxWidth = Math.max(220, cw * 0.72);
	                const lx = margin + maxWidth / 2;
	                const ly =
	                    aspectPreset === "vertical"
	                        ? ch * 0.76
	                        : aspectPreset === "square"
	                        ? ch * 0.72
	                        : ch * 0.68;

	                LayoutService.ensureTextClip({
	                    name: labelName,
	                    aspectPreset,
	                    startSec: absRange.startSec,
	                    endSec: absRange.endSec,
	                    x: lx,
	                    y: ly,
	                    maxWidth,
	                    textAlign: "left",
	                    defaultText: "Speaker Name\nSubtitle",
	                    defaultFontSize: font,
	                    defaultFontWeight: "bold",
	                });
	            }

            if (layout === "captions" && sceneInfo) {
                const absRange =
                    LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec) ?? {
                        startSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineStartSec ?? 0),
                        endSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineEndSec ?? 0),
                    };

                const captionsName = LayoutService.buildLayoutManagedClipName({
                    packId,
                    layout: "captions",
                    role: "clip",
                    sceneKey: sceneInfo.sceneKey,
                    aspectPreset,
                });

                const yPos =
                    aspectPreset === "vertical"
                        ? ch * 0.82
                        : aspectPreset === "square"
                        ? ch * 0.84
                        : ch * 0.86;

                LayoutService.ensureCaptionsClip({
                    name: captionsName,
                    aspectPreset,
                    scriptClipId: String(scriptClip.id),
                    startSec: absRange.startSec,
                    endSec: absRange.endSec,
                    x: cw / 2,
                    y: yPos,
                    maxWidth: Math.max(240, cw * 0.85),
	                });
	            }
	        } else if (layout === "screen") {
                // Screen: background "screen" layer + foreground circular webcam (script preferred).
                const nonScriptLayers = getVisibleNonScriptLayersInScene();

                const eligibleScriptSources = getOrderedEligibleScriptSourceIndices();

                const hideNonScriptExcept = (keepClipId: string | null) => {
                    for (const l of nonScriptLayers) {
                        if (keepClipId && l.clipId === keepClipId) continue;
                        try { CompositionService.setSceneVisualOverridesForClip(l.clip, sceneKey, { alpha: 0 }); } catch {}
                    }
                };

                const applyFitToNonScript = (layer: NonScriptLayer) => {
                    const clip = layer.clip;
                    const dims = (() => {
                        try {
                            if (typeof clip?.getOriginalSourceDimensions === "function") {
                                const d = clip.getOriginalSourceDimensions();
                                const w = Number(d?.width ?? 0);
                                const h = Number(d?.height ?? 0);
                                if (w > 0 && h > 0) return { w, h };
                            }
                        } catch {}
                        const w = Number((clip as any)?.width ?? 0);
                        const h = Number((clip as any)?.height ?? 0);
                        if (w > 0 && h > 0) return { w, h };
                        return null;
                    })();
	                    if (!dims) return;
	                    const s = fitScale(dims.w, dims.h, cw, ch);
	                    // Layout application should not mutate the clip-global crop; clear any per-scene crop override instead.
	                    try { CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["crop"], [sceneKey]); } catch {}
	                    try {
	                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, {
	                            x: cw / 2,
	                            y: ch / 2,
	                            width: dims.w * s,
	                            height: dims.h * s,
	                            alpha: 1,
	                            maskShape: "none",
	                        });
	                    } catch {}
	                };

                const applyCirclePipToSource = (srcIdx: number) => {
                    const dims = getDims(srcIdx);
                    if (!dims) return;
                    const pipSize = ch * 0.25;
                    const margin = Math.max(12, Math.min(cw, ch) * 0.02);
                    const trackId = getTrackIdForSource(srcIdx);
                    const focusRect = getFocusRectForTrackId(trackId, { scriptSourceIndex: srcIdx });
                    let focusTimeSec = clipLocalFocusTimeSec;
                    try {
                        const mapped = LayoutService.mapScriptVideoClipLocalToSourceTime(scriptClip, srcIdx, clipLocalFocusTimeSec);
                        if (mapped != null && Number.isFinite(mapped)) focusTimeSec = Math.max(0, mapped);
                    } catch {}

                    let faceRect: { x: number; y: number; w: number; h: number } | null = null;
                    try {
                        const keyframe: any = FocusStore.getKeyframeAtTimeSec(trackId, focusTimeSec);
                        const fr = keyframe?.face?.faceRect ?? null;
                        if (fr && typeof fr === "object") {
                            faceRect = {
                                x: Number(fr.x ?? 0),
                                y: Number(fr.y ?? 0),
                                w: Number(fr.w ?? 0),
                                h: Number(fr.h ?? 0),
                            };
                        }
                    } catch {}

                    const crop =
                        faceRect
                            ? computeSquareCropFromFaceRect({ srcW: dims.w, srcH: dims.h, faceRect, paddingFactor: 3.0 })
                            : computeCoverCropFromFocus({
                                  srcAspect: dims.w / dims.h,
                                  targetAspect: 1,
                                  focusRect,
                              });
                    applyToSource(srcIdx, {
                        width: pipSize,
                        height: pipSize,
                        x: margin + pipSize / 2,
                        y: ch - margin - pipSize / 2,
                        alpha: 1,
                        maskShape: "circle",
                        crop,
                    });
                };

                const pickTopNonScriptScreenLayer = (): NonScriptLayer | null => {
                    const screens = nonScriptLayers.filter((l) => l.isScreen);
                    return (screens[0] ?? nonScriptLayers[0] ?? null) as any;
                };

                const absRange =
                    sceneInfo
                        ? LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec)
                        : null;
                const fallbackAbsRange = absRange ?? { startSec: sceneTimelineStart, endSec: sceneTimelineEnd };

                if (eligibleScriptSources.length >= 2) {
                    const camIdx = eligibleScriptSources.find((i) => !isScreenSource(i)) ?? eligibleScriptSources[0];
                    const screenIdx =
                        eligibleScriptSources.find((i) => i !== camIdx && isScreenSource(i)) ??
                        eligibleScriptSources.find((i) => i !== camIdx) ??
                        camIdx;

                    // Screen background: fit + center.
                    const screenDims = getDims(screenIdx);
                    if (screenDims) {
                        const s = fitScale(screenDims.w, screenDims.h, cw, ch);
                        applyToSource(screenIdx, {
                            width: screenDims.w * s,
                            height: screenDims.h * s,
                            x: cw / 2,
                            y: ch / 2,
                            alpha: 1,
                            maskShape: "none",
                            crop: undefined,
                        });
                    }

                    applyCirclePipToSource(camIdx);

                    // Hide other script sources and all non-script layers.
                    for (let i = 0; i < sources.length; i++) {
                        if (i !== screenIdx && i !== camIdx) applyToSource(i, { alpha: 0 });
                    }
                    hideNonScriptExcept(null);
                } else if (eligibleScriptSources.length === 1) {
                    const camIdx = eligibleScriptSources[0];
                    const bg = pickTopNonScriptScreenLayer();
                    if (bg) {
                        applyFitToNonScript(bg);
                        hideNonScriptExcept(bg.clipId);
                    } else if (sceneInfo) {
                        const placeholderName = LayoutService.buildLayoutManagedClipName({
                            packId,
                            layout: "screen",
                            role: "placeholder",
                            sceneKey: sceneInfo.sceneKey,
                            aspectPreset,
                        });
                        const margin = Math.max(12, Math.min(cw, ch) * 0.02);
                        LayoutService.ensurePlaceholderClip({
                            name: placeholderName,
                            aspectPreset,
                            startSec: fallbackAbsRange.startSec,
                            endSec: fallbackAbsRange.endSec,
                            x: cw / 2,
                            y: ch / 2,
                            width: Math.max(120, cw - margin * 2),
                            height: Math.max(120, ch - margin * 2),
                            label: "Add media",
                            placeholderKind: "screen",
                        });
                    }

                    applyCirclePipToSource(camIdx);

                    // Hide all other script sources.
                    for (let i = 0; i < sources.length; i++) {
                        if (i !== camIdx) applyToSource(i, { alpha: 0 });
                    }
                }
	        } else if (layout === "media") {
                // Media: speaker (script preferred) + a media layer in the remaining region.
                const nonScriptLayers = getVisibleNonScriptLayersInScene();

                const eligibleScriptSources = getOrderedEligibleScriptSourceIndices();

                const hideNonScriptExcept = (keepClipId: string | null) => {
                    for (const l of nonScriptLayers) {
                        if (keepClipId && l.clipId === keepClipId) continue;
                        try { CompositionService.setSceneVisualOverridesForClip(l.clip, sceneKey, { alpha: 0 }); } catch {}
                    }
                };

                const applyCoverToNonScript = (layer: NonScriptLayer, region: { x: number; y: number; w: number; h: number }) => {
                    const clip = layer.clip;
                    const dims = (() => {
                        try {
                            if (typeof clip?.getOriginalSourceDimensions === "function") {
                                const d = clip.getOriginalSourceDimensions();
                                const w = Number(d?.width ?? 0);
                                const h = Number(d?.height ?? 0);
                                if (w > 0 && h > 0) return { w, h };
                            }
                        } catch {}
                        const w = Number((clip as any)?.width ?? 0);
                        const h = Number((clip as any)?.height ?? 0);
                        if (w > 0 && h > 0) return { w, h };
                        return null;
                    })();
                    if (!dims) return;

	                    const focusRect = getFocusRectForTrackId(layer.trackId);
		                    const crop = computeCoverCropFromFocus({
		                        srcAspect: dims.w / dims.h,
		                        targetAspect: region.w / region.h,
	                        focusRect,
	                    });
	                    try {
	                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, {
	                            x: region.x,
	                            y: region.y,
	                            width: region.w,
	                            height: region.h,
	                            alpha: 1,
	                            maskShape: "none",
	                            crop,
	                        });
	                    } catch {}
	                };

                const applyCoverToSource = (srcIdx: number, region: { x: number; y: number; w: number; h: number }, extra?: any) => {
                    const dims = getDims(srcIdx);
                    if (!dims) return;
	                    const trackId = getTrackIdForSource(srcIdx);
	                    const focusRect = getFocusRectForTrackId(trackId, { scriptSourceIndex: srcIdx });
	                    const crop = computeCoverCropFromFocus({
	                        srcAspect: dims.w / dims.h,
	                        targetAspect: region.w / region.h,
	                        focusRect,
                    });
                    applyToSource(srcIdx, {
                        x: region.x,
                        y: region.y,
                        width: region.w,
                        height: region.h,
                        alpha: 1,
                        maskShape: "none",
                        crop,
                        ...(extra ?? {}),
                    });
                };

                const applyCirclePipToSource = (srcIdx: number) => {
                    const dims = getDims(srcIdx);
                    if (!dims) return;
                    const pipSize = (aspectPreset === "square" ? ch * 0.28 : ch * 0.25);
                    const margin = Math.max(12, Math.min(cw, ch) * 0.02);
	                    const trackId = getTrackIdForSource(srcIdx);
	                    const focusRect = getFocusRectForTrackId(trackId, { scriptSourceIndex: srcIdx });
	                    const crop = computeCoverCropFromFocus({
	                        srcAspect: dims.w / dims.h,
	                        targetAspect: 1,
	                        focusRect,
	                    });
	                    applyToSource(srcIdx, {
	                        width: pipSize,
	                        height: pipSize,
                        x: margin + pipSize / 2,
                        y: ch - margin - pipSize / 2,
                        alpha: 1,
                        maskShape: "circle",
                        crop,
                    });
                };

                const pickTopNonScriptMediaLayer = (): NonScriptLayer | null => nonScriptLayers[0] ?? null;

                const absRange =
                    sceneInfo
                        ? LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec)
                        : null;
                const fallbackAbsRange = absRange ?? { startSec: sceneTimelineStart, endSec: sceneTimelineEnd };

                const margin = Math.max(12, Math.min(cw, ch) * 0.02);

                // Resolve which layers to use.
                if (eligibleScriptSources.length >= 2) {
                    const speakerIdx = eligibleScriptSources.find((i) => !isScreenSource(i)) ?? eligibleScriptSources[0];
                    const mediaIdx =
                        eligibleScriptSources.find((i) => i !== speakerIdx && isScreenSource(i)) ??
                        eligibleScriptSources.find((i) => i !== speakerIdx) ??
                        speakerIdx;

                    if (aspectPreset === "vertical" || aspectPreset === "square") {
                        // Speaker pip + media fill behind.
                        applyCirclePipToSource(speakerIdx);
                        applyCoverToSource(mediaIdx, { x: cw / 2, y: ch / 2, w: Math.max(120, cw - margin * 2), h: Math.max(120, ch - margin * 2) });
                    } else {
                        const speakerRegionW = cw * 0.4;
                        const leftW = Math.max(1, cw - speakerRegionW);
                        applyCoverToSource(speakerIdx, { x: cw - speakerRegionW / 2, y: ch / 2, w: Math.max(120, speakerRegionW - margin * 2), h: Math.max(120, ch - margin * 2) });
                        applyCoverToSource(mediaIdx, { x: leftW / 2, y: ch / 2, w: Math.max(120, leftW - margin * 2), h: Math.max(120, ch - margin * 2) });
                    }

                    // Hide unused script sources and all non-script layers.
                    for (let i = 0; i < sources.length; i++) {
                        if (i !== speakerIdx && i !== mediaIdx) applyToSource(i, { alpha: 0 });
                    }
                    hideNonScriptExcept(null);
                } else if (eligibleScriptSources.length === 1) {
                    const speakerIdx = eligibleScriptSources[0];
                    const mediaLayer = pickTopNonScriptMediaLayer();

                    if (aspectPreset === "vertical" || aspectPreset === "square") {
                        applyCirclePipToSource(speakerIdx);

                        const region = { x: cw / 2, y: ch / 2, w: Math.max(120, cw - margin * 2), h: Math.max(120, ch - margin * 2) };
                        if (mediaLayer) {
                            applyCoverToNonScript(mediaLayer, region);
                            hideNonScriptExcept(mediaLayer.clipId);
                        } else if (sceneInfo) {
                            const placeholderName = LayoutService.buildLayoutManagedClipName({
                                packId,
                                layout: "media",
                                role: "placeholder",
                                sceneKey: sceneInfo.sceneKey,
                                aspectPreset,
                            });
                            LayoutService.ensurePlaceholderClip({
                                name: placeholderName,
                                aspectPreset,
                                startSec: fallbackAbsRange.startSec,
                                endSec: fallbackAbsRange.endSec,
                                x: region.x,
                                y: region.y,
                                width: region.w,
                                height: region.h,
                                label: "Add media",
                                placeholderKind: "media",
                            });
                        }
                    } else {
                        const speakerRegionW = cw * 0.4;
                        const leftW = Math.max(1, cw - speakerRegionW);

                        applyCoverToSource(speakerIdx, { x: cw - speakerRegionW / 2, y: ch / 2, w: Math.max(120, speakerRegionW - margin * 2), h: Math.max(120, ch - margin * 2) });

                        const region = { x: leftW / 2, y: ch / 2, w: Math.max(120, leftW - margin * 2), h: Math.max(120, ch - margin * 2) };
                        if (mediaLayer) {
                            applyCoverToNonScript(mediaLayer, region);
                            hideNonScriptExcept(mediaLayer.clipId);
                        } else if (sceneInfo) {
                            const placeholderName = LayoutService.buildLayoutManagedClipName({
                                packId,
                                layout: "media",
                                role: "placeholder",
                                sceneKey: sceneInfo.sceneKey,
                                aspectPreset,
                            });
                            LayoutService.ensurePlaceholderClip({
                                name: placeholderName,
                                aspectPreset,
                                startSec: fallbackAbsRange.startSec,
                                endSec: fallbackAbsRange.endSec,
                                x: region.x,
                                y: region.y,
                                width: region.w,
                                height: region.h,
                                label: "Add media",
                                placeholderKind: "media",
                            });
                        }
                    }

                    // Hide any other script sources.
                    for (let i = 0; i < sources.length; i++) {
                        if (i !== speakerIdx) applyToSource(i, { alpha: 0 });
                    }
                }
	        } else if (layout === "chapter") {
            const srcIdx = getSourceIndexForSingle();
            const dims = getDims(srcIdx);
            if (!dims) return;

	            const margin = Math.max(12, Math.min(cw, ch) * 0.02);

	            // Horizontal: speaker on right. Vertical: smaller speaker centered lower.
	            let regionW = cw * 0.42;
	            if (aspectPreset === "square") regionW = cw * 0.45;

	            if (aspectPreset === "vertical") {
	                const videoRegionW = cw * 0.52;
	                const videoRegionH = ch * 0.34;
	                const targetW = Math.max(1, videoRegionW - margin * 2);
	                const targetH = Math.max(1, videoRegionH - margin * 2);
	                const s = fitScale(dims.w, dims.h, targetW, targetH);
	                const w = dims.w * s;
	                const h = dims.h * s;
	                applyToSource(srcIdx, {
	                    width: w,
	                    height: h,
	                    x: cw / 2,
	                    y: ch * 0.64,
	                    alpha: 1,
	                    maskShape: "none",
	                });
	            } else {
	                const targetW = Math.max(1, regionW - margin * 2);
	                const targetH = Math.max(1, ch - margin * 2);
	                const s = coverScale(dims.w, dims.h, targetW, targetH);
	                const w = dims.w * s;
	                const h = dims.h * s;
	                applyToSource(srcIdx, {
	                    width: w,
	                    height: h,
	                    x: cw - regionW / 2,
	                    y: ch / 2,
	                    alpha: 1,
	                    maskShape: "none",
	                });
	            }
            hideOtherSources(srcIdx);

            // Add chapter title text (scene-scoped).
            if (sceneInfo) {
                const absRange =
                    LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec) ?? {
                        startSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineStartSec ?? 0),
                        endSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineEndSec ?? 0),
                    };

                const base = Math.min(cw, ch);
                const titleFont = Math.max(22, Math.round(base * 0.06));

                const titleName = LayoutService.buildLayoutManagedClipName({
                    packId,
                    layout: "chapter",
                    role: "title",
                    sceneKey: sceneInfo.sceneKey,
                    aspectPreset,
                });

	                if (aspectPreset === "vertical") {
	                    const margin = Math.max(16, cw * 0.07);
	                    const maxWidth = Math.max(240, cw * 0.88);
	                    LayoutService.ensureTextClip({
	                        name: titleName,
	                        aspectPreset,
	                        startSec: absRange.startSec,
	                        endSec: absRange.endSec,
	                        x: margin + maxWidth / 2,
	                        y: ch * 0.20,
	                        maxWidth,
	                        textAlign: "left",
	                        defaultText: "This is a\nChapter Title",
	                        defaultFontSize: titleFont,
	                        defaultFontWeight: "bold",
	                    });
	                } else {
                    // Horizontal: place text in the left region.
                    const leftRegionW = Math.max(1, cw - regionW);
                    LayoutService.ensureTextClip({
                        name: titleName,
                        aspectPreset,
                        startSec: absRange.startSec,
                        endSec: absRange.endSec,
                        x: leftRegionW / 2,
                        y: ch * 0.32,
                        maxWidth: Math.max(240, leftRegionW * 0.9),
                        textAlign: "left",
                        defaultText: "Chapter title",
                        defaultFontSize: titleFont,
                        defaultFontWeight: "bold",
                    });
                }
            }
        } else if (layout === "intro") {
            // Intro: hide the script-layer visual(s) so text-only overlays can show cleanly.
            for (let i = 0; i < sources.length; i++) {
                applyToSource(i, { alpha: 0 });
            }

            if (sceneInfo) {
                const absRange =
                    LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec) ?? {
                        startSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineStartSec ?? 0),
                        endSec: Number(scriptClip?.start?.seconds ?? 0) + Number((scriptClip.getScenes?.() ?? [])[sceneIndex]?.timelineEndSec ?? 0),
                    };

                const base = Math.min(cw, ch);
                const titleFont = Math.max(28, Math.round(base * 0.085));
                const subtitleFont = Math.max(18, Math.round(base * 0.048));

                const titleName = LayoutService.buildLayoutManagedClipName({
                    packId,
                    layout: "intro",
                    role: "title",
                    sceneKey: sceneInfo.sceneKey,
                    aspectPreset,
                });
                const subtitleName = LayoutService.buildLayoutManagedClipName({
                    packId,
                    layout: "intro",
                    role: "subtitle",
                    sceneKey: sceneInfo.sceneKey,
                    aspectPreset,
                });

                const maxWidth = Math.max(240, cw * 0.85);
                const titleY = aspectPreset === "vertical" ? ch * 0.38 : ch * 0.42;
                const subtitleY = aspectPreset === "vertical" ? ch * 0.52 : ch * 0.56;

                LayoutService.ensureTextClip({
                    name: titleName,
                    aspectPreset,
                    startSec: absRange.startSec,
                    endSec: absRange.endSec,
                    x: cw / 2,
                    y: titleY,
                    maxWidth,
                    textAlign: "center",
                    defaultText: "Title",
                    defaultFontSize: titleFont,
                    defaultFontWeight: "bold",
                });

                const subtitleStart = Math.min(absRange.endSec - 0.05, absRange.startSec + 0.25);
                LayoutService.ensureTextClip({
                    name: subtitleName,
                    aspectPreset,
                    startSec: subtitleStart,
                    endSec: absRange.endSec,
                    x: cw / 2,
                    y: subtitleY,
                    maxWidth,
                    textAlign: "center",
                    defaultText: "Subtitle",
                    defaultFontSize: subtitleFont,
                    defaultFontWeight: "normal",
                });
            }
        } else {
            // Multicam variants are encoded as "multicam:<key>" where <key> is aspect-specific (e.g. two, two_screen).
            if (layout.startsWith("multicam")) {
                const parts = layout.split(":");
                const variantRaw = (parts[1] ?? "").trim();

                // Resolve scene key + absolute/timeline ranges.
                const sceneKey = sceneInfo?.sceneKey ?? "__start__";
                let sceneTimelineStart = 0;
                let sceneTimelineEnd = 0;
                let absRange: { startSec: number; endSec: number } | null = null;
                try {
                    const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
                    const s =
                        scenes.find((x: any) => Number(x?.index) === Number(sceneIndex)) ??
                        scenes[sceneIndex];
                    const clipStartAbs = Number((scriptClip as any)?.start?.seconds ?? 0);
                    const t0 = Number(s?.timelineStartSec ?? 0);
                    const t1 = Number(s?.timelineEndSec ?? t0);
                    sceneTimelineStart = clipStartAbs + (Number.isFinite(t0) ? t0 : 0);
                    sceneTimelineEnd = clipStartAbs + (Number.isFinite(t1) ? t1 : sceneTimelineStart);
                } catch {}
                try {
                    if (sceneInfo) {
                        absRange =
                            LayoutService.getSceneAbsTimeRangeFromSegments(scriptClip, sceneInfo.srcStartSec, sceneInfo.srcEndSec) ??
                            null;
                    }
                } catch {}
                if (!absRange) absRange = { startSec: sceneTimelineStart, endSec: sceneTimelineEnd };

                // Gather layout-relevant layers: script sources + non-script media clips in this scene.
                // Only user-hidden layers are excluded; layout-hidden layers remain eligible.
                type LayoutLayer =
                    | { kind: "scriptSource"; sourceIndex: number; isScreen: boolean; label: string; trackId: string | null }
                    | { kind: "clip"; clip: any; clipId: string; isScreen: boolean; label: string; trackId: string | null };

                const layers: LayoutLayer[] = [];

                // Script sources
                for (let i = 0; i < sources.length; i++) {
                    try {
                        if (isScriptSourceHiddenByUser(i)) continue;

                        const trackId = getTrackIdForSource(i);
                        const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
                        const entries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
                        const entry = entries?.[i];
                        const cfg = entry?.input ? cfgs.find((c: any) => c?.input === entry.input) : undefined;
                        const name = String(cfg?.name ?? LayoutService.getTrackRecordByTrackId(trackId ?? undefined)?.title ?? `Source ${i + 1}`);

                        layers.push({
                            kind: "scriptSource",
                            sourceIndex: i,
                            isScreen: isScreenSource(i),
                            label: `Script: ${name}`,
                            trackId,
                        });
                    } catch {}
                }

                // Non-script clips in this scene.
                try {
                    const compAny: any = CompositionService.composition;
                    const overlaps = (start: number, end: number) => start < sceneTimelineEnd && end > sceneTimelineStart;
                    for (const tr of compAny?.tracks ?? []) {
                        for (const c of (tr as any)?.clips ?? []) {
                            if (!c) continue;
                            if (String((c as any).id) === String((scriptClip as any).id)) continue;

	                            const type = String((c as any).type ?? "");
	                            if (!(type === "video" || type === "image" || type === "placeholder")) continue;

                            const name = String((c as any).name ?? "");
                            const isLayoutManaged = name.startsWith(LayoutService.LAYOUT_MANAGED_PREFIX);
                            if (isLayoutManaged) continue;

                            const start = Number((c as any)?.start?.seconds ?? 0);
                            const dur = Number((c as any)?.timelineDuration?.seconds ?? 0);
                            const end = start + Math.max(0, Number.isFinite(dur) ? dur : 0);
                            if (!overlaps(start, end)) continue;

                            const ov =
                                (c as any)?.sceneVisualOverridesByAspect?.[aspectPreset]?.[sceneKey] ??
                                (c as any)?.sceneVisualOverrides?.[sceneKey];
                            if (ov && typeof ov === "object" && (ov as any).hiddenByUser === true) continue;

                            const trackId = String((c as any)?.source?.id ?? "");
                            const track = LayoutService.getTrackRecordByTrackId(trackId);
                            const isScreen =
                                typeof track?.isScreenRecording === "boolean"
                                    ? Boolean(track.isScreenRecording)
                                    : /screen/i.test(String(track?.title ?? name));

                            layers.push({
                                kind: "clip",
                                clip: c,
                                clipId: String((c as any).id),
                                isScreen,
                                label: name || `${type} clip`,
                                trackId: trackId || null,
                            });
                        }
                    }
                } catch {}

                const hasAnyScreen = layers.some((l) => l.isScreen);
                const inferVariant = (): string => {
                    const count = layers.length;
                    if (aspectPreset === "horizontal") {
                        if (hasAnyScreen && count >= 3) {
                            const cams = Math.max(0, count - 1);
                            const base = cams <= 2 ? "two" : cams === 3 ? "three" : cams === 4 ? "four" : "four";
                            return `${base}_screen`;
                        }
                        if (count <= 2) return "two";
                        if (count === 3) return "three";
                        if (count === 4) return "four";
                        if (count === 5) return "five";
                        return "six";
                    }
                    if (count <= 2) return "two";
                    if (count === 3) return "three";
                    return "four";
                };

                const variant = variantRaw || inferVariant();
                const wantsScreen = variant.includes("_screen");
                const clipLocalFocusTimeSec =
                    sceneInfo && Number.isFinite(Number(sceneInfo.srcStartSec)) && Number.isFinite(Number(sceneInfo.srcEndSec))
                        ? Math.max(0, (Number(sceneInfo.srcStartSec) + Number(sceneInfo.srcEndSec)) / 2)
                        : 0;

                const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

                const getFocusTimeSecForLayer = (layer: LayoutLayer): number => {
                    if (layer.kind === "scriptSource") {
                        const mapped = LayoutService.mapScriptVideoClipLocalToSourceTime(
                            scriptClip,
                            layer.sourceIndex,
                            clipLocalFocusTimeSec
                        );
                        return mapped != null && Number.isFinite(mapped) ? Math.max(0, mapped) : 0;
                    }
                    // For non-script layers, fall back to the beginning of the track.
                    return 0;
                };

                const computeCoverCropFromFocus = (args: {
                    srcAspect: number;
                    targetAspect: number;
                    focusRect: { x: number; y: number; w: number; h: number } | null;
                }): { x: number; y: number; width: number; height: number } | undefined => {
                    const srcAspect = args.srcAspect;
                    const targetAspect = args.targetAspect;
                    if (!Number.isFinite(srcAspect) || !Number.isFinite(targetAspect) || targetAspect <= 0) return undefined;

                    let cropW = 1;
                    let cropH = 1;
                    if (srcAspect > targetAspect) {
                        cropW = clamp01(targetAspect / srcAspect);
                        cropH = 1;
                    } else {
                        cropW = 1;
                        cropH = clamp01(srcAspect / targetAspect);
                    }

                    const focus = args.focusRect;
                    const fx0 = clamp01(Number(focus?.x ?? 0.5));
                    const fy0 = clamp01(Number(focus?.y ?? 0.5));
                    const fw = clamp01(Number(focus?.w ?? 0));
                    const fh = clamp01(Number(focus?.h ?? 0));
                    const fx1 = clamp01(fx0 + fw);
                    const fy1 = clamp01(fy0 + fh);
                    const fcx = clamp01(fx0 + (fx1 - fx0) / 2);
                    const fcy = clamp01(fy0 + (fy1 - fy0) / 2);

                    const minX = Math.max(0, fx1 - cropW);
                    const maxX = Math.min(1 - cropW, fx0);
                    const minY = Math.max(0, fy1 - cropH);
                    const maxY = Math.min(1 - cropH, fy0);

                    const desiredX = fcx - cropW / 2;
                    const desiredY = fcy - cropH / 2;

                    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
                    const x = minX <= maxX ? clamp(desiredX, minX, maxX) : clamp(desiredX, 0, 1 - cropW);
                    const y = minY <= maxY ? clamp(desiredY, minY, maxY) : clamp(desiredY, 0, 1 - cropH);

                    if (cropW >= 0.999 && cropH >= 0.999) return undefined;
                    return { x, y, width: cropW, height: cropH };
                };

                const getLayerFocusRect = (layer: LayoutLayer): { x: number; y: number; w: number; h: number } | null => {
                    const trackId = layer.trackId;
                    if (!trackId) return null;
                    const tSec = getFocusTimeSecForLayer(layer);
                    const rect =
                        FocusStore.getRectAtTimeSec(trackId, tSec) ??
                        FocusStore.getQuickSummary(trackId)?.rect ??
                        null;
                    if (!rect) return null;
                    return { x: Number(rect.x ?? 0), y: Number(rect.y ?? 0), w: Number(rect.w ?? 0), h: Number(rect.h ?? 0) };
                };

                const applyTileToLayer = (layer: LayoutLayer, region: { left: number; top: number; w: number; h: number }) => {
                    const targetAspect = region.w / region.h;
                    if (layer.kind === "scriptSource") {
                        const dims = getDims(layer.sourceIndex);
                        if (!dims) return;
                        const focusRect = getLayerFocusRect(layer);
                        const crop = computeCoverCropFromFocus({
                            srcAspect: dims.w / dims.h,
                            targetAspect,
                            focusRect,
                        });
                        applyToSource(layer.sourceIndex, {
                            x: region.left + region.w / 2,
                            y: region.top + region.h / 2,
                            width: region.w,
                            height: region.h,
                            alpha: 1,
                            maskShape: "none",
                            crop,
                        });
                        return;
                    }

                    const clip = layer.clip;
                    const clipDims = (() => {
                        try {
                            if (typeof clip?.getOriginalSourceDimensions === "function") {
                                const d = clip.getOriginalSourceDimensions();
                                const w = Number(d?.width ?? 0);
                                const h = Number(d?.height ?? 0);
                                if (w > 0 && h > 0) return { w, h };
                            }
                        } catch {}
                        const w = Number((clip as any)?.width ?? 0);
                        const h = Number((clip as any)?.height ?? 0);
                        if (w > 0 && h > 0) return { w, h };
                        return null;
                    })();
                    if (!clipDims) return;

                    const focusRect = getLayerFocusRect(layer);
	                    const crop = computeCoverCropFromFocus({
	                        srcAspect: clipDims.w / clipDims.h,
	                        targetAspect,
	                        focusRect,
	                    });
	                    try {
	                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, {
	                            x: region.left + region.w / 2,
	                            y: region.top + region.h / 2,
	                            width: region.w,
	                            height: region.h,
	                            alpha: 1,
	                            crop,
	                        });
	                    } catch {}
	                };

                const applyFitToLayer = (layer: LayoutLayer, region: { left: number; top: number; w: number; h: number }) => {
                    if (layer.kind === "scriptSource") {
                        const dims = getDims(layer.sourceIndex);
                        if (!dims) return;
                        const s = fitScale(dims.w, dims.h, region.w, region.h);
                        applyToSource(layer.sourceIndex, {
                            x: region.left + region.w / 2,
                            y: region.top + region.h / 2,
                            width: dims.w * s,
                            height: dims.h * s,
                            alpha: 1,
                            maskShape: "none",
                            crop: undefined,
                        });
                        return;
                    }

	                    const clip = layer.clip;
	                    const isPlaceholder = String((clip as any)?.type ?? "") === "placeholder";
	                    if (isPlaceholder) {
	                        try { CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["crop"], [sceneKey]); } catch {}
	                        try {
	                            CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, {
	                                x: region.left + region.w / 2,
	                                y: region.top + region.h / 2,
	                                width: region.w,
	                                height: region.h,
	                                alpha: 1,
	                            });
                        } catch {}
                        return;
                    }

                    const dims = (() => {
                        try {
                            if (typeof clip?.getOriginalSourceDimensions === "function") {
                                const d = clip.getOriginalSourceDimensions();
                                const w = Number(d?.width ?? 0);
                                const h = Number(d?.height ?? 0);
                                if (w > 0 && h > 0) return { w, h };
                            }
                        } catch {}
                        const w = Number((clip as any)?.width ?? 0);
                        const h = Number((clip as any)?.height ?? 0);
                        if (w > 0 && h > 0) return { w, h };
                        return null;
                    })();
	                    if (!dims) return;

	                    const s = fitScale(dims.w, dims.h, region.w, region.h);
	                    try { CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["crop"], [sceneKey]); } catch {}
	                    try {
	                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, {
	                            x: region.left + region.w / 2,
	                            y: region.top + region.h / 2,
	                            width: dims.w * s,
	                            height: dims.h * s,
	                            alpha: 1,
	                        });
                    } catch {}
                };

                // Build slot regions.
                const slotRegions: Array<{ left: number; top: number; w: number; h: number }> = [];
                const pushGrid = (cols: number, rows: number, maxCells: number) => {
                    const cellW = cw / cols;
                    const cellH = ch / rows;
                    for (let r = 0; r < rows; r++) {
                        for (let c = 0; c < cols; c++) {
                            if (slotRegions.length >= maxCells) return;
                            slotRegions.push({ left: c * cellW, top: r * cellH, w: cellW, h: cellH });
                        }
                    }
                };

                const hideUnusedLayer = (layer: LayoutLayer) => {
                    if (layer.kind === "scriptSource") {
                        applyToSource(layer.sourceIndex, { alpha: 0 });
                    } else {
                        try { CompositionService.setSceneVisualOverridesForClip(layer.clip, sceneKey, { alpha: 0 }); } catch {}
                    }
                };

                const panelOrder = ScriptLayerStore.getLayoutVisualOrder(String((scriptClip as any).id));
                const panelIndex = new Map(panelOrder.map((id, i) => [id, i]));
                const getRowIdForLayer = (layer: LayoutLayer): string => {
                    if (layer.kind === "scriptSource") {
                        return `script:${String((scriptClip as any).id)}:${layer.sourceIndex}`;
                    }
                    return String((layer as any).clipId ?? "");
                };

                const sorted = [...layers]
                    .map((layer, originalIndex) => {
                        const rowId = getRowIdForLayer(layer);
                        const orderIndex = panelIndex.has(rowId) ? Number(panelIndex.get(rowId)) : Number.POSITIVE_INFINITY;
                        return { layer, originalIndex, orderIndex };
                    })
                    .sort((a, b) => {
                        const aIsPh = a.layer.kind === "clip" && String((a.layer as any).clip?.type ?? "") === "placeholder";
                        const bIsPh = b.layer.kind === "clip" && String((b.layer as any).clip?.type ?? "") === "placeholder";
                        if (aIsPh !== bIsPh) return aIsPh ? 1 : -1;

                        if (a.layer.kind !== b.layer.kind) return a.layer.kind === "scriptSource" ? -1 : 1;

                        if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
                        return a.originalIndex - b.originalIndex;
                    })
                    .map((x) => x.layer);

                const useLayers: LayoutLayer[] = [...sorted];

                // Determine slot plan based on variant.
                if (wantsScreen) {
                    const leftW = cw * 0.67;
                    const rightW = cw - leftW;
                    const screenRegion = { left: 0, top: 0, w: leftW, h: ch };

                    const camCountWanted =
                        variant.startsWith("two_") ? 2 :
                        variant.startsWith("three_") ? 3 :
                        variant.startsWith("four_") ? 4 :
                        2;

                    if (camCountWanted <= 3) {
                        const rowH = ch / camCountWanted;
                        for (let i = 0; i < camCountWanted; i++) slotRegions.push({ left: leftW, top: i * rowH, w: rightW, h: rowH });
                    } else {
                        const cellW = rightW / 2;
                        const cellH = ch / 2;
                        slotRegions.push({ left: leftW, top: 0, w: cellW, h: cellH });
                        slotRegions.push({ left: leftW + cellW, top: 0, w: cellW, h: cellH });
                        slotRegions.push({ left: leftW, top: cellH, w: cellW, h: cellH });
                        slotRegions.push({ left: leftW + cellW, top: cellH, w: cellW, h: cellH });
                    }

                    // Choose screen layer.
                    const screenIdx = useLayers.findIndex((l) => l.isScreen);
                    const screenLayer = screenIdx >= 0 ? useLayers.splice(screenIdx, 1)[0] : useLayers.shift();
                    if (screenLayer) {
                        applyFitToLayer(screenLayer, screenRegion);
                    } else {
                        // No layers at all: make a placeholder for the screen slot.
                        const screenSlotName = LayoutService.buildLayoutManagedClipName({
                            packId,
                            layout: "multicam",
                            role: `slot_0`,
                            sceneKey,
                            aspectPreset,
                        });
                        LayoutService.ensurePlaceholderClip({
                            name: screenSlotName,
                            aspectPreset,
                            startSec: absRange.startSec,
                            endSec: absRange.endSec,
                            x: screenRegion.left + screenRegion.w / 2,
                            y: screenRegion.top + screenRegion.h / 2,
                            width: screenRegion.w,
                            height: screenRegion.h,
                            label: "Add media",
                            placeholderKind: "screen",
                        });
                    }

                    const used: LayoutLayer[] = [];
                    // Assign camera slots
                    for (let i = 0; i < slotRegions.length; i++) {
                        const layer = useLayers.shift();
                        if (layer) {
                            applyTileToLayer(layer, slotRegions[i]);
                            used.push(layer);
                        } else {
                            const slotName = LayoutService.buildLayoutManagedClipName({
                                packId,
                                layout: "multicam",
                                role: `slot_${i + 1}`,
                                sceneKey,
                                aspectPreset,
                            });
                            LayoutService.ensurePlaceholderClip({
                                name: slotName,
                                aspectPreset,
                                startSec: absRange.startSec,
                                endSec: absRange.endSec,
                                x: slotRegions[i].left + slotRegions[i].w / 2,
                                y: slotRegions[i].top + slotRegions[i].h / 2,
                                width: slotRegions[i].w,
                                height: slotRegions[i].h,
                                label: "Add media",
                                placeholderKind: "camera",
                            });
                        }
                    }

                    // Hide overflow layers not used.
                    for (const l of useLayers) hideUnusedLayer(l);

                    try { void CompositionService.composition.recomputeFrameAtCurrentTimeAsync?.(); } catch {}
                    try { CompositionService.notifyClipUpdated(scriptClip); } catch {}
                    try { ScriptLayerStore.bumpVisualPropsVersion(); } catch {}
                    return;
                }

                // No-screen variants: place N tiles (and placeholders for missing).
                const countWanted =
                    variant === "two" ? 2 :
                    variant === "three" ? 3 :
                    variant === "four" ? 4 :
                    variant === "five" ? 5 :
                    variant === "six" ? 6 :
                    2;

                if (aspectPreset === "vertical") {
                    if (countWanted === 2) pushGrid(1, 2, 2);
                    else if (countWanted === 3) pushGrid(1, 3, 3);
                    else pushGrid(2, 2, Math.min(countWanted, 4));
                } else if (aspectPreset === "square") {
                    if (countWanted === 2) pushGrid(2, 1, 2);
                    else pushGrid(2, 2, Math.min(countWanted, 4));
                } else {
                    if (countWanted === 2) pushGrid(2, 1, 2);
                    else if (countWanted === 3) pushGrid(3, 1, 3);
                    else if (countWanted === 4) pushGrid(2, 2, 4);
                    else if (countWanted === 5) pushGrid(3, 2, 5);
                    else pushGrid(3, 2, 6);
                }

                const used: LayoutLayer[] = [];
                for (let i = 0; i < slotRegions.length; i++) {
                    const layer = useLayers.shift();
                    if (layer) {
                        applyTileToLayer(layer, slotRegions[i]);
                        used.push(layer);
                    } else {
                        const slotName = LayoutService.buildLayoutManagedClipName({
                            packId,
                            layout: "multicam",
                            role: `slot_${i}`,
                            sceneKey,
                            aspectPreset,
                        });
                        LayoutService.ensurePlaceholderClip({
                            name: slotName,
                            aspectPreset,
                            startSec: absRange.startSec,
                            endSec: absRange.endSec,
                            x: slotRegions[i].left + slotRegions[i].w / 2,
                            y: slotRegions[i].top + slotRegions[i].h / 2,
                            width: slotRegions[i].w,
                            height: slotRegions[i].h,
                            label: "Add media",
                            placeholderKind: "camera",
                        });
                    }
                }

                for (const l of useLayers) hideUnusedLayer(l);

                try { void CompositionService.composition.recomputeFrameAtCurrentTimeAsync?.(); } catch {}
                try { CompositionService.notifyClipUpdated(scriptClip); } catch {}
                try { ScriptLayerStore.bumpVisualPropsVersion(); } catch {}
                return;
            }

            // Other layouts are persisted but not yet applied as transforms.
            return;
        }

        try { void CompositionService.composition.recomputeFrameAtCurrentTimeAsync?.(); } catch {}

        try {
            CompositionService.notifyClipUpdated(scriptClip);
        } catch {}
        try { ScriptLayerStore.bumpVisualPropsVersion(); } catch {}
    }
}
